import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import {
  getInstructorPublicProfileBySlug,
  getOrganizationPublicProfile,
} from '../utils/publicProfiles';
import {
  createBookingRequest,
  getPublicInstructorAvailability,
} from '../utils/booking';
import { getOrganizationServicePackages, getUserActivePackages } from '../utils/servicePackages';
import { canBookService, canViewPublicService } from '../utils/platformAccess';
import {
  getPublicInstructorServices,
  getServicePricingSummary,
  isServiceEligibleForPackage,
} from '../utils/services';
import {
  buildPackageSelectionView,
  buildInstructorPublicProfile,
  buildServicePublicPage,
  getPackageSelectionLink,
  getMostPopularPackageForDisplay,
} from '../utils/clientFacingExperience';

function PublicInstructorProfilePage() {
  const { publicSlug = '' } = useParams();
  const { currentUser, userData } = useAuth() || {};
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [organizationProfile, setOrganizationProfile] = useState(null);
  const [services, setServices] = useState([]);
  const [activePackages, setActivePackages] = useState([]);
  const [servicePackages, setServicePackages] = useState([]);
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [bookingMessage, setBookingMessage] = useState('');
  const [bookingError, setBookingError] = useState('');
  const [bookingForm, setBookingForm] = useState({
    requesterName: userData?.name || '',
    requesterEmail: currentUser?.email || userData?.email || '',
    requesterPhone: '',
    serviceId: '',
    discipline: '',
    serviceType: '',
    requestedStartAt: '',
    requestedEndAt: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    notes: '',
  });

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      try {
        const nextProfile = await getInstructorPublicProfileBySlug(db, publicSlug);
        setProfile(nextProfile);

        if (nextProfile?.organizationId) {
          try {
            const nextOrganizationProfile = await getOrganizationPublicProfile(db, nextProfile.organizationId);
            setOrganizationProfile(nextOrganizationProfile?.isPublic ? nextOrganizationProfile : null);
          } catch (error) {
            setOrganizationProfile(null);
          }
        } else {
          setOrganizationProfile(null);
        }
        if (nextProfile?.userId) {
          const [nextAvailability, nextServices, nextPackages, nextServicePackages] = await Promise.all([
            getPublicInstructorAvailability(db, nextProfile.userId),
            getPublicInstructorServices(db, nextProfile.userId),
            currentUser?.uid && nextProfile?.organizationId
              ? getUserActivePackages(db, currentUser.uid, { organizationId: nextProfile.organizationId })
              : Promise.resolve([]),
            nextProfile?.organizationId ? getOrganizationServicePackages(db, nextProfile.organizationId) : Promise.resolve([]),
          ]);
          setAvailabilitySlots(nextAvailability);
          setServices(nextServices.filter(canViewPublicService));
          setActivePackages(nextPackages || []);
          setServicePackages(nextServicePackages || []);
        } else {
          setAvailabilitySlots([]);
          setServices([]);
          setActivePackages([]);
          setServicePackages([]);
        }
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [currentUser?.uid, publicSlug]);

  if (loading) {
    return <div className="mx-auto max-w-4xl p-6 text-sm text-slate-600">Loading instructor profile...</div>;
  }

  if (!profile) {
    return <div className="mx-auto max-w-4xl p-6 text-sm text-slate-600">This instructor profile is unavailable.</div>;
  }

  const selectedService = services.find((service) => service.serviceId === bookingForm.serviceId) || null;
  const publicProfile = buildInstructorPublicProfile(profile, { services });
  const packageSelection = buildPackageSelectionView(selectedService || services[0] || {}, {
    servicePackages,
    memberships: activePackages,
  });
  const mostPopularPackage = getMostPopularPackageForDisplay(selectedService || services[0] || {}, {
    servicePackages,
  });

  const handleSelectService = (service) => {
    const pricingSummary = getServicePricingSummary(service, { memberships: activePackages });
    setBookingForm((current) => ({
      ...current,
      serviceId: service.serviceId,
      discipline: service.discipline || current.discipline,
      serviceType: service.serviceType || current.serviceType,
    }));
    setBookingMessage(
      `${service.name} selected${pricingSummary.price > 0 ? ` · ${pricingSummary.price} ${pricingSummary.currency}` : ''}.`
    );
    setBookingError('');
  };

  const handleBookingSubmit = async () => {
    try {
      const result = await createBookingRequest(db, {
        organizationId: profile.organizationId,
        instructorId: profile.userId,
        requesterUserId: currentUser?.uid || '',
        requesterName: bookingForm.requesterName,
        requesterEmail: bookingForm.requesterEmail,
        requesterPhone: bookingForm.requesterPhone,
        serviceId: selectedService?.serviceId || '',
        serviceNameSnapshot: selectedService?.name || '',
        serviceDurationSnapshot: selectedService?.durationMinutes || null,
        servicePricingSnapshot: selectedService?.price ?? null,
        servicePackageEligible: Boolean(selectedService?.packageEligible),
        applicablePackageIds: selectedService?.applicablePackageIds || [],
        discipline: bookingForm.discipline,
        serviceType: bookingForm.serviceType,
        requestedStartAt: bookingForm.requestedStartAt,
        requestedEndAt: bookingForm.requestedEndAt,
        timezone: bookingForm.timezone,
        notes: bookingForm.notes,
        sourceType: 'instructor_profile',
        actorUserId: currentUser?.uid || '',
        actorName: userData?.name || currentUser?.email || bookingForm.requesterName,
      });
      setBookingMessage(result.duplicate ? 'A matching pending booking request already exists.' : 'Booking request submitted successfully.');
      setBookingError('');
    } catch (error) {
      console.error(error);
      setBookingError('Could not submit the booking request right now.');
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      <section className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Instructor</p>
            <h1 className="mt-3 text-3xl font-semibold text-slate-950">{publicProfile.displayName}</h1>
            {publicProfile.shortBio ? <p className="mt-3 text-lg text-slate-700">{publicProfile.shortBio}</p> : null}
          </div>
          {profile.featured ? (
            <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">Featured</span>
          ) : null}
        </div>

        {organizationProfile ? (
          <p className="mt-4 text-sm text-slate-600">
            Organization:{' '}
            <Link className="text-blue-600 hover:underline" to={`/orgs/${organizationProfile.publicSlug}`}>
              {organizationProfile.publicName}
            </Link>
          </p>
        ) : null}

        {profile.bio ? <p className="mt-6 text-sm leading-6 text-slate-600">{profile.bio}</p> : null}

        <div className="mt-6 flex flex-wrap gap-2 text-sm text-slate-600">
          {(profile.disciplines || []).map((discipline) => (
            <span key={discipline} className="rounded-full bg-blue-50 px-3 py-1 text-blue-700">{discipline}</span>
          ))}
          {(profile.specialties || []).map((specialty) => (
            <span key={specialty} className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">{specialty}</span>
          ))}
          {(profile.languages || []).map((language) => (
            <span key={language} className="rounded-full bg-slate-100 px-3 py-1">{language}</span>
          ))}
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Experience</p>
            <p className="mt-2 text-lg font-semibold text-slate-950">{publicProfile.experienceIndicator}</p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Public Offerings</p>
            <p className="mt-2 text-lg font-semibold text-slate-950">{publicProfile.lessonCountIndicator}</p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Specialties</p>
            <p className="mt-2 text-sm text-slate-700">{publicProfile.specialtiesDisplay.join(', ') || 'General coaching support'}</p>
          </div>
        </div>

        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl bg-slate-50 p-4">
            <h2 className="text-sm font-semibold text-slate-900">Who this lesson is for</h2>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              {publicProfile.whoItsFor.map((item) => (
                <li key={item}>• {item}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4">
            <h2 className="text-sm font-semibold text-slate-900">What you'll learn</h2>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              {publicProfile.whatYoullLearn.map((item) => (
                <li key={item}>• {item}</li>
              ))}
            </ul>
          </div>
        </div>

        {(profile.certifications || []).length > 0 ? (
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-slate-900">Certifications</h2>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              {profile.certifications.map((certification) => (
                <li key={certification}>• {certification}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>

      <section className="mt-8 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl font-semibold text-slate-950">Services</h2>
          <span className="text-sm text-slate-500">{services.length} public</span>
        </div>
        {services.length === 0 ? (
          <p className="mt-4 text-sm text-slate-600">No public services are published yet.</p>
        ) : (
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {services.map((service) => {
              const servicePage = buildServicePublicPage(service, {
                memberships: activePackages,
                servicePackages,
              });
              const pricingSummary = getServicePricingSummary(service, { memberships: activePackages }) || {
                price: service.price || 0,
                currency: service.currency || 'USD',
                durationMinutes: service.durationMinutes || 60,
                paymentHint: `${service.price || 0} ${service.currency || 'USD'}`,
              };
              const activePackageEligible = activePackages.some((membership) => isServiceEligibleForPackage(service, membership));
              return (
                <div key={service.serviceId} className="rounded-2xl border border-slate-200 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-slate-900">{service.name}</h3>
                      {service.shortDescription ? (
                        <p className="mt-1 text-sm text-slate-600">{service.shortDescription}</p>
                      ) : null}
                    </div>
                    {service.packageEligible ? (
                      <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">Package eligible</span>
                    ) : null}
                  </div>
                  <p className="mt-3 text-sm text-slate-600">
                    {pricingSummary.paymentHint} · {service.durationMinutes} min · {service.serviceType || 'Session'}
                  </p>
                  <div className="mt-3 space-y-2 text-sm text-slate-600">
                    {servicePage.whoItsFor[0] ? <p><span className="font-semibold text-slate-900">Who it's for:</span> {servicePage.whoItsFor[0]}</p> : null}
                    {servicePage.whatYoullLearn[0] ? <p><span className="font-semibold text-slate-900">What you'll learn:</span> {servicePage.whatYoullLearn[0]}</p> : null}
                    {servicePage.recommendedPackage ? (
                      <p><span className="font-semibold text-slate-900">Recommended package:</span> {servicePage.recommendedPackage.name || servicePage.recommendedPackage.summary}</p>
                    ) : null}
                  </div>
                  {service.packageEligible ? (
                    <p className="mt-2 text-xs text-slate-500">
                      {activePackageEligible
                        ? 'One of your active packages may apply to this service.'
                        : 'Package coverage may be available for this service.'}
                    </p>
                  ) : null}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {canBookService(service) ? (
                      <button
                        onClick={() => handleSelectService(service)}
                        className="rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white"
                      >
                        Book Single Lesson
                      </button>
                    ) : null}
                    {servicePage.recommendedPackage ? (
                      <Link
                        to={currentUser
                          ? getPackageSelectionLink(servicePage.recommendedPackage, {
                            service,
                            instructor: profile,
                            organizationId: profile.organizationId || '',
                          })
                          : `/login?redirect=${encodeURIComponent(getPackageSelectionLink(servicePage.recommendedPackage, {
                            service,
                            instructor: profile,
                            organizationId: profile.organizationId || '',
                          }))}`}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700"
                      >
                        Buy Package
                      </Link>
                    ) : null}
                    <Link
                      to={currentUser ? '/notifications?tab=messages' : '/login'}
                      className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700"
                    >
                      Message Instructor
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {servicePackages.length > 0 ? (
        <section className="mt-8 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-slate-950">Package options</h2>
              <p className="mt-1 text-sm text-slate-600">
                Compare the best package fit for {selectedService?.name || services[0]?.name || 'this service'} before you book.
              </p>
            </div>
            {mostPopularPackage ? (
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                {mostPopularPackage.badge}: {mostPopularPackage.name}
              </span>
            ) : null}
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {packageSelection.packages.slice(0, 3).map((servicePackage) => (
              <div key={servicePackage.servicePackageId} className="rounded-2xl border border-slate-200 p-4">
                <div className="flex flex-wrap gap-2">
                  {servicePackage.isRecommended ? <span className="rounded-full bg-sky-50 px-2 py-1 text-xs font-medium text-sky-700">Recommended for you</span> : null}
                  {servicePackage.isMostPopular ? <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">Most popular package</span> : null}
                  {servicePackage.isBestValue ? <span className="rounded-full bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">Best value package</span> : null}
                </div>
                <h3 className="mt-3 font-semibold text-slate-900">{servicePackage.name}</h3>
                <p className="mt-1 text-sm text-slate-600">{servicePackage.description || servicePackage.savings.summary}</p>
                <p className="mt-3 text-sm text-slate-600">{servicePackage.price} {servicePackage.currency} · {servicePackage.includedSessionCount || servicePackage.includedCredits || 0} included</p>
                <p className="mt-2 text-xs text-slate-500">{servicePackage.savings.summary}</p>
                <Link
                  to={currentUser
                    ? getPackageSelectionLink(servicePackage, {
                      service: selectedService || services[0] || null,
                      instructor: profile,
                      organizationId: profile.organizationId || '',
                    })
                    : `/login?redirect=${encodeURIComponent(getPackageSelectionLink(servicePackage, {
                      service: selectedService || services[0] || null,
                      instructor: profile,
                      organizationId: profile.organizationId || '',
                    }))}`}
                  className="mt-4 rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white"
                >
                  Package Selection
                </Link>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="mt-8 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
        <h2 className="text-xl font-semibold text-slate-950">Request a Booking</h2>
        {bookingMessage ? <p className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{bookingMessage}</p> : null}
        {bookingError ? <p className="mt-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{bookingError}</p> : null}
        {bookingMessage && currentUser ? (
          <p className="mt-3 text-sm text-slate-600">
            <Link className="text-blue-600 hover:underline" to="/my-bookings">Open My Bookings</Link>
          </p>
        ) : null}
        {selectedService ? (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-900">{selectedService.name}</p>
            <p className="mt-1 text-xs text-slate-500">
              {selectedService.durationMinutes} min · {selectedService.price} {selectedService.currency}
              {selectedService.packageEligible ? ' · Package eligible' : ''}
            </p>
          </div>
        ) : (
          <p className="mt-4 text-sm text-slate-600">Choose a service first for a more complete booking request.</p>
        )}
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <input value={bookingForm.requesterName} onChange={(event) => setBookingForm((current) => ({ ...current, requesterName: event.target.value }))} placeholder="Your name" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <input value={bookingForm.requesterEmail} onChange={(event) => setBookingForm((current) => ({ ...current, requesterEmail: event.target.value }))} placeholder="Your email" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <input value={bookingForm.requesterPhone} onChange={(event) => setBookingForm((current) => ({ ...current, requesterPhone: event.target.value }))} placeholder="Your phone" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <input value={bookingForm.discipline} onChange={(event) => setBookingForm((current) => ({ ...current, discipline: event.target.value }))} placeholder="Discipline" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <input value={bookingForm.serviceType} onChange={(event) => setBookingForm((current) => ({ ...current, serviceType: event.target.value }))} placeholder="Service type" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <input value={bookingForm.timezone} onChange={(event) => setBookingForm((current) => ({ ...current, timezone: event.target.value }))} placeholder="Timezone" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <input value={bookingForm.requestedStartAt} onChange={(event) => setBookingForm((current) => ({ ...current, requestedStartAt: event.target.value }))} placeholder="Requested start" type="datetime-local" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <input value={bookingForm.requestedEndAt} onChange={(event) => setBookingForm((current) => ({ ...current, requestedEndAt: event.target.value }))} placeholder="Requested end" type="datetime-local" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm" />
          <textarea value={bookingForm.notes} onChange={(event) => setBookingForm((current) => ({ ...current, notes: event.target.value }))} placeholder="Notes" rows={4} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm md:col-span-2" />
        </div>
        <div className="mt-4">
          <button onClick={handleBookingSubmit} className="rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white">
            Submit Booking Request
          </button>
        </div>
      </section>

      <section className="mt-8 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
        <h2 className="text-xl font-semibold text-slate-950">Public Availability</h2>
        {(availabilitySlots || []).length === 0 ? (
          <p className="mt-4 text-sm text-slate-600">No public availability is published yet. You can still submit a booking request.</p>
        ) : (
          <div className="mt-4 space-y-3">
            {(availabilitySlots || []).slice(0, 6).map((slot) => (
              <div key={slot.slotId} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-600">
                {slot.specificDate || slot.dayOfWeek || 'Flexible'} · {slot.startTime || 'Start TBD'} - {slot.endTime || 'End TBD'} · {slot.timezone}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default PublicInstructorProfilePage;
